import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CartItem } from './components/CartItem';
import { CartTotal } from './components/CartTotal';
import { AppDispatch } from '../../../app/store/store';
import { deleteItemFromCart } from '../../../entities/cart/model';

interface Props {
  cartItems: any[];
  total: number;
  setCartSteps: (step: string) => void;
}

export function CartStepYourCart({ cartItems, total, setCartSteps }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());

  const allSelected = cartItems.length > 0 && selectedItems.size === cartItems.length;
  const someSelected = selectedItems.size > 0 && selectedItems.size < cartItems.length;

  const handleSelectAll = (checked: boolean) => {
    setSelectedItems(checked ? new Set(cartItems.map((item) => item.id)) : new Set());
  };

  const handleSelectItem = (itemId: number, checked: boolean) => {
    const next = new Set(selectedItems);
    if (checked) next.add(itemId);
    else next.delete(itemId);
    setSelectedItems(next);
  };

  const handleDeleteSelected = () => {
    if (selectedItems.size === 0) return;
    const ids = Array.from(selectedItems);
    setSelectedItems(new Set());
    ids.forEach((id) => dispatch(deleteItemFromCart(id)));
  };

  return (
    <div className="w-full flex gap-6 mt-7">
      {/* Left Section */}
      <div className="flex-1">
        {/* Select All Header */}
        <div className="bg-white rounded-2xl px-5 py-3 mb-4 flex items-center justify-between shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <AppCheckbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
            />
            <span className="text-sm font-medium text-gray-700">Выбрать все</span>
          </label>
          {selectedItems.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-2 text-sm font-medium text-[#e3282a] hover:text-red-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Удалить выбранные ({selectedItems.size})
            </button>
          )}
        </div>

        {/* Cart Items List */}
        <div className="space-y-4">
          {cartItems.map((item) => (
            <CartItem
              key={item.id}
              item={item}
              isSelected={selectedItems.has(item.id)}
              onSelectChange={(checked) => handleSelectItem(item.id, checked)}
            />
          ))}
        </div>
      </div>

      {/* Right Section - Order Summary */}
      <div className="w-[400px] flex-shrink-0">
        <CartTotal cartItems={cartItems} total={total} setCartSteps={setCartSteps} />
      </div>
    </div>
  );
}

interface AppCheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
}

function AppCheckbox({ checked, indeterminate = false, onChange }: AppCheckboxProps) {
  return (
    <label className="relative flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        ref={(el) => { if (el) el.indeterminate = indeterminate; }}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
          checked || indeterminate
            ? 'bg-[#e3282a] border-[#e3282a]'
            : 'bg-white border-gray-300 hover:border-[#e3282a]'
        }`}
      >
        {indeterminate && !checked ? (
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
          </svg>
        ) : checked ? (
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : null}
      </div>
    </label>
  );
}